import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  CloudDownload as CloudDownloadIcon,
  List as ListIcon,
  Print as PrintIcon,
  FileDownload as FileDownloadIcon,
  EditNote as EditNoteIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { getApiBaseUrlWithFallback, API_BASE_URL } from '../config/environment';
import { collectSegmentLinksFromIndexDom } from '../utils/liveScoreIndex';

export default function InterclubCompetitions({ interclubId }) {
  const { getIdToken, isAdmin } = useFirebaseAuth();
  
  const [competitions, setCompetitions] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [saving, setSaving] = useState(false);
  const [liveScoreProcessingId, setLiveScoreProcessingId] = useState(null);
  const [liveScoreSegmentSystem, setLiveScoreSegmentSystem] = useState('both');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [resultsViewOpen, setResultsViewOpen] = useState(false);
  const [resultsViewData, setResultsViewData] = useState([]);
  const [resultsViewLoading, setResultsViewLoading] = useState(false);
  const [resultsViewTitle, setResultsViewTitle] = useState('');
  const [resultsViewSource, setResultsViewSource] = useState('');
  const [resultsViewDocId, setResultsViewDocId] = useState(null);
  const [resultsViewComp, setResultsViewComp] = useState(null);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [overrideCsvText, setOverrideCsvText] = useState('');
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [overrideError, setOverrideError] = useState(null);
  const [rowMarkSavingIdx, setRowMarkSavingIdx] = useState(null);

  // Planned attendance state
  const [plannedCounts, setPlannedCounts] = useState({});
  const [plannedSkaters, setPlannedSkaters] = useState({});
  const [plannedDialogOpen, setPlannedDialogOpen] = useState(false);
  const [selectedPlannedComp, setSelectedPlannedComp] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    shortName: '',
    date: '',
    startDate: '',
    endDate: '',
    location: '',
    hostClubId: '',
    seasonId: '',
    description: '',
    registrationLink: '',
    liveScoreLink: '',
    clubWebsiteLink: '',
    status: 'scheduled',
  });

  const loadClubs = useCallback(async () => {
    try {
      const baseUrl = await getApiBaseUrlWithFallback();
      const response = await fetch(`${baseUrl}/interclubs/${interclubId}/clubs`);
      if (response.ok) {
        const data = await response.json();
        setClubs(data || []);
      }
    } catch (err) {
      console.error('Error loading clubs:', err);
    }
  }, [interclubId]);

  const loadSeasons = useCallback(async () => {
    try {
      const baseUrl = await getApiBaseUrlWithFallback();
      const response = await fetch(`${baseUrl}/seasons?interclubId=${interclubId}`);
      
      if (response.ok) {
        const data = await response.json();
        const filteredSeasons = (data || []).filter(s => s.interclubId === interclubId);
        setSeasons(filteredSeasons);
        
        // Select current season if available
        const current = filteredSeasons.find(s => s.isCurrent);
        if (current) {
          setSelectedSeason(current.id);
        } else if (filteredSeasons.length > 0) {
          setSelectedSeason(filteredSeasons[0].id);
        }
      }
    } catch (err) {
      console.error('Error loading seasons:', err);
    }
  }, [interclubId]);

  const loadCompetitions = useCallback(async () => {
    if (!interclubId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const baseUrl = await getApiBaseUrlWithFallback();
      let url = `${baseUrl}/competitions?interclubId=${interclubId}`;
      if (selectedSeason) {
        url += `&seasonId=${selectedSeason}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Failed to fetch competitions');
      }
      
      const data = await response.json();
      // Filter to only show competitions for this interclub
      const filtered = (data || []).filter(c => c.interclubId === interclubId);
      setCompetitions(filtered);
    } catch (err) {
      console.error('Error loading competitions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [interclubId, selectedSeason]);

  const loadPlannedAttendance = useCallback(async () => {
    if (!interclubId) return;
    
    try {
      const token = await getIdToken();
      if (!token) return;
      
      const baseUrl = await getApiBaseUrlWithFallback();
      let url = `${baseUrl}/interclubs/${interclubId}/planned-attendance`;
      if (selectedSeason) {
        url += `?seasonId=${selectedSeason}`;
      }
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setPlannedCounts(data.plannedCounts || {});
        setPlannedSkaters(data.plannedSkaters || {});
      }
    } catch (err) {
      console.error('Error loading planned attendance:', err);
    }
  }, [interclubId, selectedSeason, getIdToken]);

  useEffect(() => {
    loadClubs();
    loadSeasons();
  }, [loadClubs, loadSeasons]);

  useEffect(() => {
    loadCompetitions();
  }, [loadCompetitions]);

  useEffect(() => {
    loadPlannedAttendance();
  }, [loadPlannedAttendance]);

  const filteredCompetitions = competitions.filter(comp => {
    const searchLower = searchTerm.toLowerCase();
    return (
      comp.name?.toLowerCase().includes(searchLower) ||
      comp.location?.toLowerCase().includes(searchLower)
    );
  });

  // Format date from various formats (Firestore Timestamp, ISO string, etc.)
  const formatDate = (dateValue) => {
    if (!dateValue) return '—';
    
    let date;
    // Handle Firestore Timestamp format
    if (dateValue._seconds) {
      date = new Date(dateValue._seconds * 1000);
    } else if (dateValue.seconds) {
      date = new Date(dateValue.seconds * 1000);
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      return '—';
    }
    
    if (isNaN(date.getTime())) return '—';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Convert Firestore timestamp to input date format (YYYY-MM-DD)
  // Uses UTC methods to avoid timezone shifts (e.g., Mar 14 becoming Mar 13)
  const toInputDate = (dateValue) => {
    if (!dateValue) return '';
    
    // If it's already a YYYY-MM-DD string, return as-is
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    
    let date;
    if (dateValue._seconds) {
      date = new Date(dateValue._seconds * 1000);
    } else if (dateValue.seconds) {
      date = new Date(dateValue.seconds * 1000);
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      return '';
    }
    
    if (isNaN(date.getTime())) return '';
    
    // Use UTC methods to preserve the stored date without timezone conversion
    return date.getUTCFullYear() + '-' + 
           String(date.getUTCMonth() + 1).padStart(2, '0') + '-' + 
           String(date.getUTCDate()).padStart(2, '0');
  };

  const handleAddNew = () => {
    setSelectedCompetition(null);
    setFormData({
      name: '',
      shortName: '',
      date: '',
      startDate: '',
      endDate: '',
      location: '',
      hostClubId: '',
      seasonId: selectedSeason || '',
      description: '',
      registrationLink: '',
      liveScoreLink: '',
      clubWebsiteLink: '',
      status: 'scheduled',
    });
    setEditDialogOpen(true);
  };

  const handleEdit = (competition) => {
    setSelectedCompetition(competition);
    setFormData({
      name: competition.name || '',
      shortName: competition.shortName || '',
      date: toInputDate(competition.date),
      startDate: toInputDate(competition.startDate),
      endDate: toInputDate(competition.endDate),
      location: competition.location || '',
      hostClubId: competition.hostClubId || competition.clubId || '',
      seasonId: competition.seasonId || '',
      description: competition.description || '',
      registrationLink: competition.registrationLink || '',
      liveScoreLink: competition.liveScoreLink || '',
      clubWebsiteLink: competition.clubWebsiteLink || '',
      status: competition.status || 'scheduled',
    });
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (competition) => {
    setSelectedCompetition(competition);
    setDeleteDialogOpen(true);
  };

  const handleFormChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      
      const payload = {
        ...formData,
        interclubId,
      };
      
      // If date is set but not startDate/endDate, use date for both
      if (payload.date && !payload.startDate) {
        payload.startDate = payload.date;
      }
      if (payload.startDate && !payload.endDate) {
        payload.endDate = payload.startDate;
      }
      
      const url = selectedCompetition 
        ? `${API_BASE_URL}/competitions/${selectedCompetition.id}`
        : `${API_BASE_URL}/competitions`;
      
      const response = await fetch(url, {
        method: selectedCompetition ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save competition');
      }
      
      setSnackbar({
        open: true,
        message: selectedCompetition ? 'Competition updated successfully' : 'Competition created successfully',
        severity: 'success',
      });
      
      setEditDialogOpen(false);
      loadCompetitions();
    } catch (err) {
      console.error('Error saving competition:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to save competition',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCompetition) return;
    
    try {
      setSaving(true);
      
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      
      const response = await fetch(`${API_BASE_URL}/competitions/${selectedCompetition.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete competition');
      }
      
      setSnackbar({
        open: true,
        message: 'Competition deleted successfully',
        severity: 'success',
      });
      
      setDeleteDialogOpen(false);
      loadCompetitions();
    } catch (err) {
      console.error('Error deleting competition:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to delete competition',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const getResultCell = (row, key) => {
    if (row[key] !== undefined && row[key] !== null) return String(row[key]);
    if (key === 'Parsed group') return row.Group !== undefined && row.Group !== null ? String(row.Group) : '';
    const camel = key.replace(/\s+(\w)/g, (_, c) => c.toUpperCase()).replace(/^./, (c) => c.toLowerCase());
    return row[camel] !== undefined && row[camel] !== null ? String(row[camel]) : '';
  };

  const getEligibilityDisplay = (row) => {
    const eligibility = (getResultCell(row, 'Eligibility') || '').trim();
    if (eligibility === 'Eligible' && row.registeredForScoring === false) return 'Ineligible (not paid)';
    return eligibility || '';
  };

  const getPointsDisplay = (row) => {
    if (row.registeredForScoring === false) return '0';
    return getResultCell(row, 'Points');
  };

  const RESULTS_TABLE_HEADERS = ['Place', 'Parsed group', 'First Name', 'Last Name', 'Event', 'Level', 'Club', 'Points', 'Eligibility'];

  const resultsToCsv = (rows) => {
    if (!rows?.length) return '';
    const header = RESULTS_TABLE_HEADERS.join(',');
    const escape = (v) => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const body = rows.map((row) =>
      RESULTS_TABLE_HEADERS.map((key) =>
        escape(key === 'Eligibility' ? getEligibilityDisplay(row) : key === 'Points' ? getPointsDisplay(row) : getResultCell(row, key))
      ).join(',')
    ).join('\n');
    return `${header}\n${body}`;
  };

  const parseCsvToResults = (csvText) => {
    const lines = csvText.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return [];
    const parseLine = (line) => {
      const out = [];
      let cur = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') {
          if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
          else inQuotes = !inQuotes;
        } else if ((c === ',' && !inQuotes) || c === '\n') {
          out.push(cur);
          cur = '';
          if (c === '\n') break;
        } else cur += c;
      }
      out.push(cur);
      return out;
    };
    const headers = parseLine(lines[0]).map((h) => h.trim());
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const vals = parseLine(lines[i]);
      const row = {};
      headers.forEach((h, j) => { row[h] = vals[j] ?? ''; });
      rows.push(row);
    }
    return rows;
  };

  const handlePrintResults = () => { window.print(); };
  const handleExportResults = () => {
    const csv = resultsToCsv(resultsViewData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `competition-results-${(resultsViewTitle || 'export').replace(/[^a-z0-9-_]/gi, '-')}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const handleOpenOverride = () => {
    setOverrideCsvText(resultsToCsv(resultsViewData));
    setOverrideError(null);
    setOverrideDialogOpen(true);
  };

  const persistResultsRows = async (rows) => {
    if (!resultsViewDocId) return;
    const baseUrl = await getApiBaseUrlWithFallback();
    const token = await getIdToken();
    const res = await fetch(`${baseUrl}/competition-results/${resultsViewDocId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ results: rows }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || 'Failed to save.');
    setResultsViewData(Array.isArray(data.results) ? data.results : rows);
  };

  const handleMarkRowEligible = async (idx) => {
    const raw = (getResultCell(resultsViewData[idx], 'Eligibility') || '').trim();
    if (raw === 'Eligible') return;
    setRowMarkSavingIdx(idx);
    try {
      const next = resultsViewData.map((r, i) => {
        if (i !== idx) return r;
        return { ...r, Eligibility: 'Eligible', eligibility: 'Eligible' };
      });
      // Server recalculates Points from Place + Eligibility on save
      await persistResultsRows(next);
      setSnackbar({ open: true, message: 'Row marked as qualifying (Eligible); points recalculated.', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to save', severity: 'error' });
    } finally {
      setRowMarkSavingIdx(null);
    }
  };

  const handleSaveOverride = async () => {
    const parsed = parseCsvToResults(overrideCsvText);
    if (!parsed.length) {
      setOverrideError('No valid rows in CSV.');
      return;
    }
    setOverrideSaving(true);
    setOverrideError(null);
    try {
      // Server recalculates Points from Place + Eligibility on save
      await persistResultsRows(parsed);
      setOverrideDialogOpen(false);
      setSnackbar({ open: true, message: 'Results updated; points recalculated from eligibility.', severity: 'success' });
    } catch (e) {
      setOverrideError(e.message || 'Request failed.');
    } finally {
      setOverrideSaving(false);
    }
  };

  const fetchAndShowResults = async (comp) => {
    setResultsViewTitle(comp.name || 'Results');
    setResultsViewData([]);
    setResultsViewSource('');
    setResultsViewDocId(null);
    setResultsViewComp(comp);
    setResultsViewOpen(true);
    setResultsViewLoading(true);
    try {
      const baseUrl = await getApiBaseUrlWithFallback();
      const res = await fetch(`${baseUrl}/competition-results/${comp.id}`);
      const list = await res.json().catch(() => []);
      if (!res.ok) {
        setResultsViewData([]);
        setSnackbar({ open: true, message: list?.error || 'Failed to load results', severity: 'error' });
        return;
      }
      const latest = Array.isArray(list) ? list.find((r) => r.isLatest) || list[0] : null;
      setResultsViewDocId(latest?.id ?? null);
      setResultsViewData(latest?.results || []);
      const src = latest?.source;
      setResultsViewSource(src === 'live-scores' ? 'Live score' : src === 'pdf-upload' ? 'PDF upload' : 'PDF upload');
    } catch {
      setResultsViewData([]);
      setSnackbar({ open: true, message: 'Failed to load results', severity: 'error' });
    } finally {
      setResultsViewLoading(false);
    }
  };

  const getClubName = (clubId) => {
    const club = clubs.find(c => c.id === clubId);
    return club?.name || '—';
  };

  const handlePlannedClick = (comp, e) => {
    e.stopPropagation();
    setSelectedPlannedComp(comp);
    setPlannedDialogOpen(true);
  };

  return (
    <>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}>
              <CalendarIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Competitions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your interclub competition schedule
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              sx={{ 
                borderRadius: 999, 
                textTransform: 'none', 
                fontWeight: 600,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                }
              }}
            >
              Add Competition
            </Button>
            <IconButton 
              onClick={loadCompetitions} 
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Season</InputLabel>
            <Select
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
              label="Season"
              size="small"
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="">All Seasons</MenuItem>
              {seasons.map((season) => (
                <MenuItem key={season.id} value={season.id}>
                  {season.name} {season.isCurrent && '(Current)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            variant="outlined"
            placeholder="Search competitions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel size="small">Live segments</InputLabel>
            <Select
              size="small"
              label="Live segments"
              value={liveScoreSegmentSystem}
              onChange={(e) => setLiveScoreSegmentSystem(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="both">All (IJS + 6.0)</MenuItem>
              <MenuItem value="ijs">IJS only</MenuItem>
              <MenuItem value="60">Majority 6.0 only</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
            <Button onClick={loadCompetitions} sx={{ ml: 2 }} size="small">
              Retry
            </Button>
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredCompetitions.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 6,
            px: 3,
            bgcolor: 'grey.50',
            borderRadius: 3
          }}>
            <CalendarIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm ? 'No competitions found' : 'No competitions yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchTerm ? 'Try adjusting your search terms.' : 'Get started by adding your first competition.'}
            </Typography>
            {!searchTerm && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddNew}
                sx={{ 
                  borderRadius: 999, 
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                Add Your First Competition
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filteredCompetitions.map((comp) => {
              const startDate = comp.startDate || comp.date;
              const endDate = comp.endDate;
              const hasDateRange = endDate && formatDate(endDate) !== formatDate(startDate);
              
              return (
                <Paper
                  key={comp.id}
                  elevation={0}
                  sx={{ 
                    p: 2.5,
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: 3,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.light',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {/* Date indicator */}
                    <Box sx={{ 
                      minWidth: 60,
                      textAlign: 'center',
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: comp.status === 'completed' ? 'success.50' : 
                               comp.status === 'in_progress' ? 'warning.50' : 'primary.50',
                      border: '1px solid',
                      borderColor: comp.status === 'completed' ? 'success.200' : 
                                   comp.status === 'in_progress' ? 'warning.200' : 'primary.200',
                    }}>
                      <Typography variant="caption" sx={{ 
                        fontWeight: 700, 
                        color: comp.status === 'completed' ? 'success.dark' : 
                               comp.status === 'in_progress' ? 'warning.dark' : 'primary.dark',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5
                      }}>
                        {startDate ? new Date(startDate._seconds ? startDate._seconds * 1000 : startDate).toLocaleDateString('en-US', { month: 'short' }) : '—'}
                      </Typography>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 800, 
                        color: comp.status === 'completed' ? 'success.dark' : 
                               comp.status === 'in_progress' ? 'warning.dark' : 'primary.dark',
                        lineHeight: 1
                      }}>
                        {startDate ? new Date(startDate._seconds ? startDate._seconds * 1000 : startDate).getDate() : '—'}
                      </Typography>
                    </Box>

                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {comp.name}
                        </Typography>
                        <Chip
                          label={
                            comp.status === 'completed' ? 'Completed' :
                            comp.status === 'in_progress' ? 'In Progress' :
                            comp.status === 'cancelled' ? 'Cancelled' : 'Scheduled'
                          }
                          size="small"
                          sx={{ 
                            fontWeight: 600,
                            bgcolor: comp.status === 'completed' ? 'success.50' : 
                                     comp.status === 'in_progress' ? 'warning.50' :
                                     comp.status === 'cancelled' ? 'error.50' : 'grey.100',
                            color: comp.status === 'completed' ? 'success.dark' : 
                                   comp.status === 'in_progress' ? 'warning.dark' :
                                   comp.status === 'cancelled' ? 'error.dark' : 'grey.700',
                            border: '1px solid',
                            borderColor: comp.status === 'completed' ? 'success.200' : 
                                         comp.status === 'in_progress' ? 'warning.200' :
                                         comp.status === 'cancelled' ? 'error.200' : 'grey.300',
                          }}
                        />
                        {plannedCounts[comp.id] > 0 && (
                          <Tooltip title="Click to see skaters planning to attend">
                            <Chip
                              icon={<PeopleIcon sx={{ fontSize: '16px !important' }} />}
                              label={`${plannedCounts[comp.id]} planning`}
                              size="small"
                              onClick={(e) => handlePlannedClick(comp, e)}
                              sx={{ 
                                fontWeight: 600,
                                bgcolor: 'info.50',
                                color: 'info.dark',
                                border: '1px solid',
                                borderColor: 'info.200',
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: 'info.100',
                                }
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarIcon sx={{ fontSize: 16 }} />
                          {formatDate(startDate)}
                          {hasDateRange && ` – ${formatDate(endDate)}`}
                        </Typography>
                        {comp.location && (
                          <Typography variant="body2" color="text.secondary">
                            📍 {comp.location}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          🏢 {getClubName(comp.hostClubId || comp.clubId)}
                        </Typography>
                      </Box>
                      
                      {comp.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                          {comp.description}
                        </Typography>
                      )}
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View parsed results">
                        <span>
                          <IconButton
                            size="small"
                            disabled={resultsViewLoading}
                            onClick={() => fetchAndShowResults(comp)}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1.5,
                              '&:hover': { bgcolor: 'grey.100', borderColor: 'grey.400' },
                            }}
                          >
                            <ListIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {comp.liveScoreLink && (
                        <Tooltip title="Process scores from live site">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!!liveScoreProcessingId}
                              onClick={async () => {
                                if (!comp.id || !comp.liveScoreLink) return;
                                setLiveScoreProcessingId(comp.id);
                                try {
                                  const token = await getIdToken();
                                  const baseUrl = await getApiBaseUrlWithFallback();
                                  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
                                  const liveScoreLink = comp.liveScoreLink.trim();
                                  const indexProxyRes = await fetch(
                                    `${baseUrl}/proxy-live-score?url=${encodeURIComponent(liveScoreLink)}`,
                                    { headers: authHeaders }
                                  );
                                  const indexData = await indexProxyRes.json().catch(() => ({}));
                                  if (!indexProxyRes.ok) {
                                    setSnackbar({ open: true, message: indexData.error || 'Could not load index page.', severity: 'error' });
                                    return;
                                  }
                                  const indexHtml = indexData.html || '';
                                  const parser = new DOMParser();
                                  const doc = parser.parseFromString(indexHtml, 'text/html');
                                  const segmentEntries = collectSegmentLinksFromIndexDom(
                                    doc,
                                    liveScoreLink,
                                    liveScoreSegmentSystem
                                  );
                                  if (segmentEntries.length === 0) {
                                    setSnackbar({ open: true, message: 'No segment links found on the index page.', severity: 'error' });
                                    return;
                                  }
                                  const segmentHtmls = [];
                                  const batchSize = 8;
                                  for (let i = 0; i < segmentEntries.length; i += batchSize) {
                                    const batch = segmentEntries.slice(i, i + batchSize);
                                    const pages = await Promise.all(
                                      batch.map(async ({ url, label }) => {
                                        try {
                                          const r = await fetch(
                                            `${baseUrl}/proxy-live-score?url=${encodeURIComponent(url)}`,
                                            { headers: authHeaders }
                                          );
                                          const d = await r.json().catch(() => ({}));
                                          return { url, label, html: r.ok && d.html ? d.html : '' };
                                        } catch {
                                          return { url, label, html: '' };
                                        }
                                      })
                                    );
                                    segmentHtmls.push(...pages);
                                  }
                                  const res = await fetch(`${baseUrl}/process-live-scores-from-html`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      ...authHeaders,
                                    },
                                    body: JSON.stringify({
                                      competitionId: comp.id,
                                      competitionName: comp.name,
                                      segmentHtmls,
                                    }),
                                  });
                                  const data = await res.json().catch(() => ({}));
                                  if (!res.ok) {
                                    const fallback =
                                      res.status === 413
                                        ? 'This competition is too large to process in one request. Try IJS only or Majority 6.0 only.'
                                        : `Failed to process live scores (${res.status})`;
                                    setSnackbar({ open: true, message: data.error || fallback, severity: 'error' });
                                    return;
                                  }
                                  setSnackbar({
                                    open: true,
                                    message: data.message || `Ingested ${data.resultsCount ?? 0} results.`,
                                    severity: 'success',
                                  });
                                  await fetchAndShowResults(comp);
                                  loadCompetitions();
                                } catch (err) {
                                  setSnackbar({
                                    open: true,
                                    message: err.message || 'Failed to process live scores',
                                    severity: 'error',
                                  });
                                } finally {
                                  setLiveScoreProcessingId(null);
                                }
                              }}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: 'success.50', borderColor: 'success.main' },
                              }}
                            >
                              {liveScoreProcessingId === comp.id ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                <CloudDownloadIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit">
                        <IconButton 
                          size="small" 
                          onClick={() => handleEdit(comp)}
                          sx={{ 
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            '&:hover': { bgcolor: 'primary.50', borderColor: 'primary.main' }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteClick(comp)}
                          sx={{ 
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            '&:hover': { bgcolor: 'error.50', borderColor: 'error.main', color: 'error.main' }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Box sx={{ 
            p: 1, 
            borderRadius: 2, 
            bgcolor: 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <CalendarIcon sx={{ color: 'primary.dark' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {selectedCompetition ? 'Edit Competition' : 'Add New Competition'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedCompetition ? 'Update competition details' : 'Create a new competition event'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Competition Name *"
                  value={formData.name}
                  onChange={handleFormChange('name')}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Short Name"
                  value={formData.shortName}
                  onChange={handleFormChange('shortName')}
                  helperText="Optional abbreviated name"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Date *"
                  type="date"
                  value={formData.startDate}
                  onChange={handleFormChange('startDate')}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={formData.endDate}
                  onChange={handleFormChange('endDate')}
                  InputLabelProps={{ shrink: true }}
                  helperText="Leave empty for single-day events"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={formData.location}
                  onChange={handleFormChange('location')}
                  placeholder="e.g., Seattle, WA"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Host Club *</InputLabel>
                  <Select
                    value={formData.hostClubId}
                    onChange={handleFormChange('hostClubId')}
                    label="Host Club *"
                    required
                  >
                    <MenuItem value="">
                      <em>Select a club...</em>
                    </MenuItem>
                    {clubs.map((club) => (
                      <MenuItem key={club.id} value={club.id}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {club.name}
                          </Typography>
                          {club.aliases?.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Includes: {club.aliases.join(', ')}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Season *</InputLabel>
                  <Select
                    value={formData.seasonId}
                    onChange={handleFormChange('seasonId')}
                    label="Season *"
                    required
                  >
                    <MenuItem value="">
                      <em>Select a season...</em>
                    </MenuItem>
                    {seasons.map((season) => (
                      <MenuItem key={season.id} value={season.id}>
                        {season.name} {season.isCurrent && '(Current)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={handleFormChange('status')}
                    label="Status"
                  >
                    <MenuItem value="scheduled">Scheduled</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={handleFormChange('description')}
                  placeholder="Additional details about the competition..."
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Registration Link"
                  value={formData.registrationLink}
                  onChange={handleFormChange('registrationLink')}
                  placeholder="https://example.com/register"
                  helperText="Optional: Link to registration page"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Club Website Link"
                  value={formData.clubWebsiteLink}
                  onChange={handleFormChange('clubWebsiteLink')}
                  placeholder="https://example.com/club"
                  helperText="Optional: Host club website"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Live Score Link"
                  value={formData.liveScoreLink}
                  onChange={handleFormChange('liveScoreLink')}
                  placeholder="https://ijs.usfigureskating.org/..."
                  helperText="Optional: Link to live scoring or results page"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={() => setEditDialogOpen(false)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.name || !formData.hostClubId}
            sx={{ borderRadius: 2, px: 3 }}
          >
            {saving ? 'Saving...' : (selectedCompetition ? 'Save Changes' : 'Create Competition')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Competition</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedCompetition?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving}
          >
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Parsed results view dialog */}
      <Dialog
        open={resultsViewOpen}
        onClose={() => setResultsViewOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>{resultsViewTitle || 'Parsed results'}</DialogTitle>
        <DialogContent dividers>
          {resultsViewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : resultsViewData.length === 0 ? (
            <Typography color="text.secondary">No results to display. Process scores from the live site or upload a file first.</Typography>
          ) : (
            <>
              {resultsViewSource && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Source: {resultsViewSource}
                </Typography>
              )}
              {(() => {
                const eligibleRows = resultsViewData.filter(
                  (row) => (getResultCell(row, 'Eligibility') || '').trim() === 'Eligible'
                );
                const eligibleCount = eligibleRows.length;
                const registeredForScoreCount = eligibleRows.filter((row) => row.registeredForScoring === true).length;
                const notRegisteredCount = eligibleCount - registeredForScoreCount;
                return (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Summary: {resultsViewData.length} total placement rows (a skater may appear in multiple ineligible events).
                    {eligibleCount} eligible placements at this competition (at most one eligible event per skater).
                    Of those, <strong>{registeredForScoreCount} are registered for the season and receive a score</strong>;
                    {notRegisteredCount > 0 ? (
                      <> {notRegisteredCount} are not registered (or had not paid by the competition date) and do not count toward qualifiers.</>
                    ) : (
                      <> all are registered.</>
                    )}
                  </Typography>
                );
              })()}
              <TableContainer sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Place</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Parsed group</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>First Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Last Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Event</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Level</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Club</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Points</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Eligibility</TableCell>
                    {isAdmin() && resultsViewDocId && (
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Actions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultsViewData.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{getResultCell(row, 'Place')}</TableCell>
                      <TableCell>{getResultCell(row, 'Parsed group')}</TableCell>
                      <TableCell>{getResultCell(row, 'First Name')}</TableCell>
                      <TableCell>{getResultCell(row, 'Last Name')}</TableCell>
                      <TableCell>{getResultCell(row, 'Event')}</TableCell>
                      <TableCell>{getResultCell(row, 'Level')}</TableCell>
                      <TableCell>{getResultCell(row, 'Club')}</TableCell>
                      <TableCell>{getPointsDisplay(row)}</TableCell>
                      <TableCell>{getEligibilityDisplay(row)}</TableCell>
                      {isAdmin() && resultsViewDocId && (
                        <TableCell align="right">
                          {(getResultCell(row, 'Eligibility') || '').trim() !== 'Eligible' && (
                            <Tooltip title="Mark this row as qualifying (Eligible). Use when the venue shortened the event title; full CSV override is still available.">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMarkRowEligible(idx)}
                                  disabled={rowMarkSavingIdx !== null}
                                  color="primary"
                                  aria-label="Mark row as qualifying"
                                >
                                  {rowMarkSavingIdx === idx ? (
                                    <CircularProgress size={18} />
                                  ) : (
                                    <CheckCircleIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button startIcon={<PrintIcon />} onClick={handlePrintResults} disabled={resultsViewData.length === 0}>
            Print
          </Button>
          <Button startIcon={<FileDownloadIcon />} onClick={handleExportResults} disabled={resultsViewData.length === 0}>
            Export CSV
          </Button>
          {isAdmin() && resultsViewDocId && (
            <Button startIcon={<EditNoteIcon />} onClick={handleOpenOverride} disabled={resultsViewData.length === 0} color="secondary">
              Override results
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setResultsViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Admin override: edit results as CSV */}
      <Dialog open={overrideDialogOpen} onClose={() => !overrideSaving && setOverrideDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Override results</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Edit the CSV below to correct errors, then Save. Columns: Place, Parsed group, First Name, Last Name, Event, Level, Club, Points, Eligibility.
          </Typography>
          {overrideError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setOverrideError(null)}>
              {overrideError}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            minRows={12}
            maxRows={24}
            value={overrideCsvText}
            onChange={(e) => setOverrideCsvText(e.target.value)}
            placeholder="Place, Parsed group, First Name, Last Name, Event, Level, Club, Points, Eligibility"
            sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)} disabled={overrideSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveOverride} disabled={overrideSaving}>
            {overrideSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Planned Skaters Dialog */}
      <Dialog 
        open={plannedDialogOpen} 
        onClose={() => setPlannedDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              p: 1, 
              borderRadius: 2, 
              bgcolor: 'info.50',
              display: 'flex',
            }}>
              <PeopleIcon sx={{ color: 'info.main' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Skaters Planning to Attend
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedPlannedComp?.name}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPlannedComp && plannedSkaters[selectedPlannedComp.id]?.length > 0 ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                <Typography variant="body2">
                  {plannedSkaters[selectedPlannedComp.id].length} skater
                  {plannedSkaters[selectedPlannedComp.id].length !== 1 ? 's have' : ' has'} marked 
                  this event as planned in their Eligibility Tracker.
                </Typography>
              </Alert>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Skater</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Club</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plannedSkaters[selectedPlannedComp.id].map((skater) => (
                      <TableRow key={skater.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {skater.firstName} {skater.lastName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {skater.club || '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PeopleIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                No skaters have marked this event as planned yet.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPlannedDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
